import { IToothDetails } from '@dataTypes/appointment.type'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { ProcedureSettingsType } from '@dataTypes/procedureSettings.type'
import {
  IPhaseItems,
  IPhases,
  IProcedureDentalCondition,
  TxPlanStatus,
} from '@dataTypes/treatmentPlan.types'
import {
  CONDITION_CODES_WITH_SURFACE_GRID,
  CONDITIONS,
  MATERIALS,
} from '@constants/Charting/conditions.schema'
import { formatToothDetailsToSurfaceString } from '@utils/tx-plan-tooth-details.utils'
import { resolveProcedureSurfaceSelectionRules } from '@utils/procedure-surface-selection.utils'
import { txPlanPhaseAccordionEventKey } from '@utils/treatment-plan.utils'
import {
  calculateInsuranceForItem,
  convertToothDetailsToToothSurfaces,
  createEmptyPhase,
  createNewPhaseKey,
} from '@components/pages/Charting/TreatmentPlan/treatmentPlanMain.helpers'
import {
  parseTxPlanTranscript,
  TxPlanVoiceOperation,
  TxPlanVoicePhaseTarget,
} from './tx-plan-transcript.utils'

const HISTORY_LIMIT = 40

const SURFACE_WORDS: Record<string, string> = {
  m: 'm',
  d: 'd',
  b: 'b',
  f: 'b',
  o: 'o',
  i: 'i',
  p: 'p',
  l: 'l',
  mesial: 'm',
  distal: 'd',
  buccal: 'b',
  facial: 'b',
  occlusal: 'o',
  incisal: 'i',
  palatal: 'p',
  lingual: 'l',
}

const SURFACE_CLUSTERS: Record<string, string[]> = {
  mo: ['m', 'o'],
  do: ['d', 'o'],
  mi: ['m', 'i'],
  di: ['d', 'i'],
  mod: ['m', 'o', 'd'],
  mob: ['m', 'o', 'b'],
  mid: ['m', 'i', 'd'],
  mol: ['m', 'o', 'l'],
  mop: ['m', 'o', 'p'],
  dob: ['d', 'o', 'b'],
  dol: ['d', 'o', 'l'],
  dop: ['d', 'o', 'p'],
  mifl: ['m', 'i', 'b', 'l'],
  mifp: ['m', 'i', 'b', 'p'],
  difl: ['d', 'i', 'b', 'l'],
  difp: ['d', 'i', 'b', 'p'],
  mibl: ['m', 'i', 'b', 'l'],
  mibp: ['m', 'i', 'b', 'p'],
  dibl: ['d', 'i', 'b', 'l'],
  dibp: ['d', 'i', 'b', 'p'],
}

export interface TxPlanVoiceProcedure {
  procedure_setting_id: string
  procedure_id: string
  description: string
  clinic_fee?: number | string | null
  procedure?: {
    procedure_id?: string
    procedure_code?: string
    description?: string
    procedure_dental_conditions?: IProcedureDentalCondition[]
  }
}

export type TxPlanVoiceHistoryEntry = {
  phases: IPhases[]
  activeAccordionKeys: string[]
  phaseIndex: number
}

export type TxPlanVoicePendingAddMissingField = 'tooth' | 'surface' | 'material'

export type TxPlanVoicePendingAdd = {
  procedure: TxPlanVoiceProcedure
  phaseIndex: number
  originalQuery: string
  matchedText: string
  teeth: number[]
  surfaces: string[]
  material: string | null
  missingFields: TxPlanVoicePendingAddMissingField[]
}

export type TxPlanVoiceCursorState = {
  phaseIndex: number
  paused: boolean
  history: TxPlanVoiceHistoryEntry[]
  pendingAdd?: TxPlanVoicePendingAdd
}

export type TxPlanVoiceApplyResult = {
  phases: IPhases[]
  activeAccordionKeys: string[]
  state: TxPlanVoiceCursorState
  message: string
  control?: 'stop'
  phasesChanged: boolean
  rejected: boolean
  pendingStatus?: { phaseIndex: number; status: TxPlanStatus }
  pendingSearch?: { phaseIndex: number; query: string }
}

export type TxPlanVoiceApplyContext = {
  procedures: TxPlanVoiceProcedure[]
  insurance: InsurancePlan | null
  discountPercent: number
  isEditing: boolean
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const readDentalConditions = (
  value: unknown,
): IProcedureDentalCondition[] | undefined =>
  Array.isArray(value) ? (value as IProcedureDentalCondition[]) : undefined

export const readTxPlanVoiceProcedure = (
  setting: ProcedureSettingsType,
): TxPlanVoiceProcedure => {
  const nestedUnknown = (setting as { procedure?: unknown }).procedure
  const nested = isRecord(nestedUnknown) ? nestedUnknown : undefined
  return {
    procedure_setting_id: setting.procedure_setting_id,
    procedure_id: setting.procedure_id,
    description: setting.description,
    clinic_fee: setting.clinic_fee,
    procedure: nested
      ? {
          procedure_id:
            typeof nested.procedure_id === 'string'
              ? nested.procedure_id
              : undefined,
          procedure_code:
            typeof nested.procedure_code === 'string'
              ? nested.procedure_code
              : undefined,
          description:
            typeof nested.description === 'string'
              ? nested.description
              : undefined,
          procedure_dental_conditions: readDentalConditions(
            nested.procedure_dental_conditions,
          ),
        }
      : undefined,
  }
}

const clonePhases = (phases: IPhases[]): IPhases[] =>
  JSON.parse(JSON.stringify(phases)) as IPhases[]

const visibleIndexes = (phases: IPhases[]): number[] =>
  phases.reduce<number[]>((indexes, phase, index) => {
    if (!phase.is_hidden) indexes.push(index)
    return indexes
  }, [])

const firstVisibleIndex = (phases: IPhases[]): number =>
  visibleIndexes(phases)[0] ?? 0

export const createTxPlanVoiceState = (
  phases: IPhases[] = [],
): TxPlanVoiceCursorState => ({
  phaseIndex: firstVisibleIndex(phases),
  paused: false,
  history: [],
})

const pushHistory = (
  state: TxPlanVoiceCursorState,
  phases: IPhases[],
  activeAccordionKeys: string[],
): TxPlanVoiceHistoryEntry[] =>
  [
    ...state.history,
    {
      phases: clonePhases(phases),
      activeAccordionKeys: [...activeAccordionKeys],
      phaseIndex: state.phaseIndex,
    },
  ].slice(-HISTORY_LIMIT)

const phaseLabel = (phases: IPhases[], index: number): string => {
  const visible = visibleIndexes(phases)
  const position = visible.indexOf(index)
  return phases[index]?.phase_name || `phase ${position + 1}`
}

const resolvePhaseIndex = (
  phases: IPhases[],
  target: TxPlanVoicePhaseTarget | undefined,
  fallback: number,
): number | string => {
  const visible = visibleIndexes(phases)
  if (!target) {
    if (!phases[fallback] || phases[fallback].is_hidden) {
      return visible[0] ?? 'Add a phase first.'
    }
    return fallback
  }
  if (target.kind === 'index') {
    const resolved = visible[target.value - 1]
    return resolved === undefined
      ? `Phase ${target.value} is not on this plan.`
      : resolved
  }
  const name = target.value.trim().toLowerCase()
  const found = phases.findIndex(
    (phase) =>
      !phase.is_hidden && phase.phase_name.trim().toLowerCase() === name,
  )
  return found === -1 ? `Phase ${target.value} is not on this plan.` : found
}

const expandPhase = (
  phases: IPhases[],
  keys: string[],
  index: number,
): string[] => {
  const key = txPlanPhaseAccordionEventKey(phases[index], index)
  return keys.includes(key) ? keys : [...keys, key]
}

const normalizePhrase = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const procedureTexts = (
  procedure: TxPlanVoiceProcedure,
): { code: string; description: string } => ({
  code: normalizePhrase(procedure.procedure?.procedure_code ?? ''),
  description: normalizePhrase(
    procedure.procedure?.description || procedure.description || '',
  ),
})

const MATERIAL_TOKEN = new Set(Object.keys(MATERIALS))

const uniqueStrings = (values: string[]): string[] => {
  const seen: Record<string, true> = {}
  const unique: string[] = []
  values.forEach((value) => {
    if (seen[value]) return
    seen[value] = true
    unique.push(value)
  })
  return unique
}

const peelSlots = (
  tokens: string[],
): { material?: string; surfaces: string[]; unknown: string[] } => {
  let material: string | undefined
  const surfaces: string[] = []
  const unknown: string[] = []
  tokens.forEach((token) => {
    if (SURFACE_CLUSTERS[token]) {
      surfaces.push(...SURFACE_CLUSTERS[token])
      return
    }
    if (SURFACE_WORDS[token]) {
      surfaces.push(SURFACE_WORDS[token])
      return
    }
    if (MATERIAL_TOKEN.has(token)) {
      material = token
      return
    }
    unknown.push(token)
  })
  return {
    ...(material ? { material } : {}),
    surfaces: uniqueStrings(surfaces),
    unknown,
  }
}

const MATERIAL_HINTS: Record<string, string[]> = {
  zirconia: ['zirconia', 'ceramic', 'porcelain'],
  ceramic: ['ceramic', 'porcelain', 'zirconia'],
  porcelain: ['porcelain', 'ceramic', 'zirconia'],
  gold: ['gold', 'noble'],
  metal: ['metal', 'noble'],
  pfm: ['pfm', 'fused'],
  pfz: ['pfz', 'zirconia'],
  composite: ['composite', 'resin'],
  amalgam: ['amalgam'],
  titanium: ['titanium'],
}

const hasWord = (haystack: string, word: string): boolean =>
  new RegExp(
    `(?:^|\\s)${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s|$)`,
  ).test(haystack)

const descriptionHasMaterial = (
  description: string,
  material: string,
): boolean =>
  (MATERIAL_HINTS[material] ?? [material]).some((hint) =>
    hasWord(description, hint),
  )

const EXTRA_FAMILY = [
  'implant',
  'abutment',
  'pontic',
  'retainer',
  'bridge',
  'provisional',
  'recement',
  'denture',
  'crown',
]

const isPosteriorTooth = (tooth: number): boolean =>
  (tooth >= 1 && tooth <= 5) ||
  (tooth >= 12 && tooth <= 21) ||
  (tooth >= 28 && tooth <= 32)

const spokenArch = (teeth: number[]): 'anterior' | 'posterior' | null => {
  if (teeth.length === 0) return null
  const posterior = teeth.map(isPosteriorTooth)
  if (posterior.every(Boolean)) return 'posterior'
  if (posterior.every((value) => !value)) return 'anterior'
  return null
}

const descriptionArch = (
  description: string,
): 'anterior' | 'posterior' | null => {
  if (hasWord(description, 'anterior')) return 'anterior'
  if (hasWord(description, 'posterior')) return 'posterior'
  return null
}

const preferredCdtCode = (
  keywords: string[],
  material: string | undefined,
  surfaceCount: number,
  arch: 'anterior' | 'posterior' | null,
): string | null => {
  const words: Record<string, true> = {}
  keywords.forEach((word) => {
    words[word] = true
  })
  if (material) words[material] = true
  if (words.crown && !words.implant && !words.abutment && !words.pontic) {
    if (
      material === 'zirconia' ||
      material === 'ceramic' ||
      material === 'porcelain'
    ) {
      return 'd2740'
    }
  }
  if (words.implant && !words.crown && !words.abutment) return 'd6010'
  if (material !== 'composite' && !words.composite) return null
  if (arch === 'anterior') {
    if (surfaceCount === 1) return 'd2330'
    if (surfaceCount === 2) return 'd2331'
    if (surfaceCount === 3) return 'd2332'
    if (surfaceCount >= 4) return 'd2335'
  }
  if (surfaceCount === 1) return 'd2391'
  if (surfaceCount === 2) return 'd2392'
  if (surfaceCount === 3) return 'd2393'
  if (surfaceCount >= 4) return 'd2394'
  return null
}

const procedureCode = (procedure: TxPlanVoiceProcedure): string =>
  normalizePhrase(procedure.procedure?.procedure_code ?? '').replace(/\s+/g, '')

const cdtSurfaceRule = (
  procedure: TxPlanVoiceProcedure,
): { count: number; isMin: boolean } | null => {
  const code = procedureCode(procedure)
  if (['d2391', 'd2330', 'd2140'].includes(code))
    return { count: 1, isMin: false }
  if (['d2392', 'd2331', 'd2150'].includes(code))
    return { count: 2, isMin: false }
  if (['d2393', 'd2332', 'd2160'].includes(code))
    return { count: 3, isMin: false }
  if (['d2394', 'd2335', 'd2161'].includes(code))
    return { count: 4, isMin: true }
  return null
}

const matchProcedure = (
  query: string,
  procedures: TxPlanVoiceProcedure[],
  teeth: number[] = [],
):
  | {
      procedure: TxPlanVoiceProcedure
      material?: string
      surfaces: string[]
      matchedText: string
    }
  | { error: string } => {
  const normalizedQuery = normalizePhrase(query)
  if (!normalizedQuery) return { error: 'Say a procedure name or code.' }
  const slots = peelSlots(normalizedQuery.split(' '))
  const keywords = slots.unknown
  if (keywords.length === 0 && !slots.material) {
    return { error: 'Say a procedure name or code.' }
  }
  const keywordText = keywords.join(' ')
  type Candidate = {
    procedure: TxPlanVoiceProcedure
    matchedText: string
    score: number
  }
  const candidates: Candidate[] = []
  procedures.forEach((procedure) => {
    const texts = procedureTexts(procedure)
    const codeMatch =
      texts.code.length > 0 &&
      (texts.code === keywordText.replace(/\s+/g, '') ||
        texts.code === keywordText)
    const description = texts.description
    const descriptionTokens = description ? description.split(' ') : []
    const conditionWords = (
      procedure.procedure?.procedure_dental_conditions ?? []
    ).flatMap((mapping) => {
      const slug = mapping.dental_condition?.slug?.toLowerCase() ?? ''
      const name = mapping.dental_condition?.name?.toLowerCase() ?? ''
      return [slug, name].filter(Boolean)
    })
    const keywordsInDescription =
      keywords.length > 0 &&
      keywords.every((keyword) => hasWord(description, keyword))
    const keywordsInCondition =
      keywords.length > 0 &&
      keywords.every((keyword) => conditionWords.includes(keyword))
    if (!codeMatch && !keywordsInDescription && !keywordsInCondition) return
    const surfaceRules = resolveProcedureSurfaceSelectionRules(
      procedure.procedure?.procedure_dental_conditions,
      description,
    )
    const spokenSurfaceCount = slots.surfaces.length
    const surfaceBonus =
      spokenSurfaceCount > 0 && surfaceRules.surfaceCount !== null
        ? surfaceRules.surfaceCount === spokenSurfaceCount ||
          (surfaceRules.surfaceCountIsMin &&
            spokenSurfaceCount >= surfaceRules.surfaceCount)
          ? 1600
          : -1600
        : 0
    const arch = spokenArch(teeth)
    const procedureArch = descriptionArch(description)
    const archBonus =
      arch && procedureArch ? (arch === procedureArch ? 300 : -400) : 0
    const spokenWords: Record<string, true> = {}
    keywords.forEach((keyword) => {
      spokenWords[keyword] = true
    })
    if (slots.material) spokenWords[slots.material] = true
    const familyPenalty = EXTRA_FAMILY.reduce(
      (penalty, word) =>
        !spokenWords[word] && hasWord(description, word)
          ? penalty + 500
          : penalty,
      0,
    )
    const extraWords = Math.max(0, descriptionTokens.length - keywords.length)
    const materialBonus =
      slots.material && descriptionHasMaterial(description, slots.material)
        ? 500
        : 0
    const exactMaterialBonus =
      slots.material && hasWord(description, slots.material) ? 400 : 0
    const exactBonus = description === keywordText ? 500 : 0
    const score =
      (codeMatch ? 10000 : 0) +
      (keywordsInCondition ? 600 : 0) +
      exactBonus +
      materialBonus +
      exactMaterialBonus +
      surfaceBonus +
      archBonus -
      familyPenalty +
      (1000 - extraWords * 10)
    candidates.push({
      procedure,
      matchedText: codeMatch ? texts.code : keywordText,
      score,
    })
  })
  if (candidates.length === 0) {
    return { error: `No procedure matches “${query}”.` }
  }
  const bestScore = Math.max(...candidates.map((candidate) => candidate.score))
  const best = candidates.filter((candidate) => candidate.score === bestScore)
  const matchedCodes: Record<string, true> = {}
  best.forEach((candidate) => {
    const code =
      candidate.procedure.procedure?.procedure_code ||
      candidate.procedure.procedure_id
    matchedCodes[code] = true
  })
  const preferred = preferredCdtCode(
    keywords,
    slots.material,
    slots.surfaces.length,
    spokenArch(teeth),
  )
  const preferredWinner = preferred
    ? best.find(
        (candidate) => procedureTexts(candidate.procedure).code === preferred,
      )
    : undefined
  if (!preferredWinner && Object.keys(matchedCodes).length > 1) {
    return {
      error: `“${query}” matches more than one procedure. Say the code.`,
    }
  }
  const winner = preferredWinner ?? best[0]
  return {
    procedure: winner.procedure,
    matchedText: winner.matchedText,
    surfaces: slots.surfaces,
    ...(slots.material ? { material: slots.material } : {}),
  }
}

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const findPhraseSpan = (
  text: string,
  phrase: string,
): { start: number; end: number } | null => {
  const parts = phrase.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return null
  const pattern = parts.map(escapeRegExp).join('\\s+')
  const match = new RegExp(`(?:^|[^a-z0-9])(${pattern})(?![a-z0-9])`, 'i').exec(
    text,
  )
  if (match?.index === undefined || !match[1]) return null
  const start = match.index + match[0].length - match[1].length
  return { start, end: start + match[1].length }
}

export type TxPlanLiveTranscriptSegment = {
  text: string
  description?: string
}

/** Split a transcript so the matched procedure word can show its description. */
export const segmentTxPlanLiveTranscript = (
  liveText: string,
  procedures: TxPlanVoiceProcedure[],
): TxPlanLiveTranscriptSegment[] => {
  if (!liveText.trim() || procedures.length === 0) return [{ text: liveText }]
  const operation = parseTxPlanTranscript(liveText).find(
    (item): item is Extract<TxPlanVoiceOperation, { op: 'add' }> =>
      item.op === 'add' && item.procedureQuery.trim() !== '',
  )
  if (!operation) return [{ text: liveText }]
  const matched = matchProcedure(
    operation.procedureQuery,
    procedures,
    operation.teeth ?? [],
  )
  if ('error' in matched) return [{ text: liveText }]
  const description = (
    matched.procedure.procedure?.description ||
    matched.procedure.description ||
    ''
  ).trim()
  if (!description) return [{ text: liveText }]
  const span = findPhraseSpan(liveText, matched.matchedText)
  if (!span) return [{ text: liveText }]
  const spoken = liveText.slice(span.start, span.end)
  if (normalizePhrase(spoken) === normalizePhrase(description)) {
    return [{ text: liveText }]
  }
  const segments: TxPlanLiveTranscriptSegment[] = []
  if (span.start > 0) segments.push({ text: liveText.slice(0, span.start) })
  segments.push({ text: spoken, description })
  if (span.end < liveText.length) {
    segments.push({ text: liveText.slice(span.end) })
  }
  return segments
}

const requiredMaterial = (
  procedure: TxPlanVoiceProcedure,
  matchedText: string,
  spoken?: string,
): string | null | { error: string } => {
  const conditions = procedure.procedure?.procedure_dental_conditions ?? []
  const allowed = new Set<string>()
  let needed = false
  conditions.forEach((mapping) => {
    const slug = mapping.dental_condition?.slug
    if (!slug) return
    const metadata = CONDITIONS[slug]
    if (!metadata?.allowsMaterial) return
    needed = true
    ;(metadata.validMaterials ?? []).forEach((material) =>
      allowed.add(material),
    )
  })
  if (!needed) return spoken ?? conditions[0]?.default_material ?? null
  if (spoken) {
    return allowed.has(spoken)
      ? spoken
      : { error: `${spoken} is not a material for this procedure.` }
  }
  const haystack = normalizePhrase(
    `${matchedText} ${procedure.procedure?.description ?? ''} ${procedure.description}`,
  )
  const inferred = uniqueStrings(
    haystack.split(' ').filter((token) => allowed.has(token)),
  )
  if (inferred.length === 1) return inferred[0]
  return conditions[0]?.default_material ?? null
}

const expectedSurfaceCount = (
  procedure: TxPlanVoiceProcedure,
): { count: number; isMin: boolean } | null => {
  const conditions = procedure.procedure?.procedure_dental_conditions ?? []
  const description =
    procedure.procedure?.description || procedure.description || null
  const rules = resolveProcedureSurfaceSelectionRules(conditions, description)
  const codeRule = cdtSurfaceRule(procedure)
  const count = rules.surfaceCount ?? codeRule?.count ?? null
  if (count === null) return null
  return {
    count,
    isMin: rules.surfaceCountIsMin || codeRule?.isMin === true,
  }
}

const surfacesAreValid = (
  procedure: TxPlanVoiceProcedure,
  surfaces: string[],
  toothCount: number,
): string | null => {
  const conditions = procedure.procedure?.procedure_dental_conditions ?? []
  const description =
    procedure.procedure?.description || procedure.description || null
  const rules = resolveProcedureSurfaceSelectionRules(conditions, description)
  const slugNeedsSurface = conditions.some((mapping) => {
    const slug = mapping.dental_condition?.slug
    return Boolean(slug && CONDITION_CODES_WITH_SURFACE_GRID.includes(slug))
  })
  const required =
    rules.requiresSurfaceSelection ||
    slugNeedsSurface ||
    cdtSurfaceRule(procedure) !== null
  if (surfaces.length > 0 && toothCount === 0) {
    return 'Say a tooth for those surfaces.'
  }
  if (!required) return null
  if (toothCount === 0) return 'Say a tooth for this procedure.'
  // Voice allows partial surface sets for now; only block when none were said.
  if (surfaces.length === 0) return 'Say the surfaces for this procedure.'
  return null
}

const requiredMissingFields = (
  procedure: TxPlanVoiceProcedure,
  teeth: number[],
  surfaces: string[],
  material: string | null,
): TxPlanVoicePendingAddMissingField[] => {
  const missing: TxPlanVoicePendingAddMissingField[] = []
  void material
  const conditions = procedure.procedure?.procedure_dental_conditions ?? []
  const description =
    procedure.procedure?.description || procedure.description || null
  const rules = resolveProcedureSurfaceSelectionRules(conditions, description)
  const slugNeedsSurface = conditions.some((mapping) => {
    const slug = mapping.dental_condition?.slug
    return Boolean(slug && CONDITION_CODES_WITH_SURFACE_GRID.includes(slug))
  })
  const surfaceRequired =
    rules.requiresSurfaceSelection ||
    slugNeedsSurface ||
    cdtSurfaceRule(procedure) !== null
  if (surfaceRequired && teeth.length === 0) missing.push('tooth')
  if (surfaceRequired && surfaces.length === 0) missing.push('surface')
  return missing
}

const missingFieldPrompt = (
  missingFields: TxPlanVoicePendingAddMissingField[],
): string => {
  const prompts = missingFields.map((field) => {
    if (field === 'tooth') return 'Tooth?'
    if (field === 'surface') return 'Surfaces?'
    return 'Material?'
  })
  return prompts.join(' ')
}

const pendingAddLabel = (pending: TxPlanVoicePendingAdd): string =>
  (
    procedureCode(pending.procedure).toUpperCase() ||
    pending.procedure.procedure?.description ||
    pending.procedure.description ||
    pending.originalQuery
  ).trim()

const formatSpokenSurfaces = (surfaces: string[]): string =>
  surfaces.length > 0
    ? surfaces.map((surface) => surface.toUpperCase()).join(', ')
    : 'none'

const pendingSelectionSummary = (pending: TxPlanVoicePendingAdd): string => {
  const tooth = pending.teeth.length > 0 ? pending.teeth.join(', ') : 'none'
  const expected = expectedSurfaceCount(pending.procedure)
  const expectedNote = expected
    ? expected.isMin
      ? ` (need ≥${expected.count})`
      : ` (need ${expected.count})`
    : ''
  return `${pendingAddLabel(pending)} · tooth ${tooth} · surfaces ${formatSpokenSurfaces(
    pending.surfaces,
  )}${expectedNote}`
}

const promptForPendingAdd = (pending: TxPlanVoicePendingAdd): string =>
  `${pendingSelectionSummary(pending)}. ${missingFieldPrompt(pending.missingFields)}`

const mergeUniqueNumbers = (left: number[], right: number[]): number[] =>
  Array.from(new Set([...left, ...right]))

const completePendingAdd = (
  pending: TxPlanVoicePendingAdd,
  operation: Extract<TxPlanVoiceOperation, { op: 'add' }>,
): TxPlanVoicePendingAdd => {
  const slots = peelSlots(normalizePhrase(operation.procedureQuery).split(' '))
  const material = slots.material ?? pending.material
  const teeth = mergeUniqueNumbers(pending.teeth, operation.teeth ?? [])
  const surfaceOnlyFollowUp =
    teeth.length === pending.teeth.length &&
    (operation.teeth?.length ?? 0) === 0 &&
    slots.unknown.length === 0 &&
    !slots.material &&
    slots.surfaces.length > 0
  // A multi-surface reply replaces a prior attempt (e.g. MIFL then MOB).
  // A single letter still accumulates across turns.
  const surfaces = surfaceOnlyFollowUp
    ? slots.surfaces.length >= 2
      ? slots.surfaces
      : uniqueStrings([...pending.surfaces, ...slots.surfaces])
    : slots.surfaces.length > 0
      ? uniqueStrings([...pending.surfaces, ...slots.surfaces])
      : pending.surfaces
  return {
    ...pending,
    teeth,
    surfaces,
    material: material ?? null,
    missingFields: requiredMissingFields(
      pending.procedure,
      teeth,
      surfaces,
      material ?? null,
    ),
  }
}

const buildItems = (
  procedure: TxPlanVoiceProcedure,
  phaseId: string | null,
  teeth: number[],
  surfaces: string[],
  material: string | null,
  context: TxPlanVoiceApplyContext,
): IPhaseItems[] => {
  const charges = Number(procedure.clinic_fee ?? 0)
  const safeCharges = Number.isFinite(charges) ? charges : 0
  const conditions = (
    procedure.procedure?.procedure_dental_conditions ?? []
  ).filter((mapping) => mapping.dental_condition)
  const procedureSettings = {
    procedure_setting_id: procedure.procedure_setting_id,
    appointment_type_subcategory_id: '',
    procedure_id: procedure.procedure_id,
    description: procedure.description,
    fee1: 0,
    fee2: 0,
    fee3: 0,
    clinic_fee: procedure.clinic_fee,
    date_time_created_at: '',
    date_time_updated_at: '',
    procedure: procedure.procedure,
  } as ProcedureSettingsType
  const groups =
    teeth.length > 0
      ? teeth.map((tooth) => [
          {
            area_type: 1,
            tooth_num: tooth,
            ...(surfaces.length > 0 ? { surf: surfaces } : {}),
          } satisfies IToothDetails,
        ])
      : [[]]
  return groups.map((toothDetails) => {
    const draft: IPhaseItems = {
      item_id: '',
      phase_id: phaseId,
      procedure_id: procedure.procedure?.procedure_id ?? procedure.procedure_id,
      provider_id: '',
      charges: safeCharges,
      insurance: 0,
      adjustments: 0,
      outstanding: 0,
      material,
      type: conditions[0]?.default_type ?? null,
      procedure_settings: procedureSettings,
      proceduredentalcondition: conditions.length > 0 ? conditions : undefined,
      is_hidden: false,
    }
    const insurance = calculateInsuranceForItem(draft, context.insurance)
    const adjustments =
      ((safeCharges - insurance) * context.discountPercent) / 100
    const surfaceLabel = formatToothDetailsToSurfaceString(toothDetails)
    const itemId = `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const toothSurfaces = convertToothDetailsToToothSurfaces(
      toothDetails,
      itemId,
    )
    return {
      ...draft,
      item_id: itemId,
      insurance,
      adjustments,
      outstanding: safeCharges - insurance - adjustments,
      surface: surfaceLabel || null,
      tooth_surfaces: toothSurfaces.length > 0 ? toothSurfaces : undefined,
    }
  })
}

export const applyTxPlanVoiceOperations = (
  phases: IPhases[],
  activeAccordionKeys: string[],
  state: TxPlanVoiceCursorState,
  operations: TxPlanVoiceOperation[],
  context: TxPlanVoiceApplyContext,
): TxPlanVoiceApplyResult => {
  let currentPhases = phases
  let keys = activeAccordionKeys
  let current = state
  let message =
    'Listening for treatment plan commands. Pause briefly after each phrase.'
  let phasesChanged = false
  let pendingStatus: TxPlanVoiceApplyResult['pendingStatus']
  let pendingSearch: TxPlanVoiceApplyResult['pendingSearch']
  let control: 'stop' | undefined
  let rejected = false
  const blocked = (reason: string): void => {
    message = reason
    rejected = true
  }
  operations.forEach((operation) => {
    if (rejected || control) return
    if (
      current.paused &&
      operation.op !== 'resume' &&
      operation.op !== 'stop' &&
      operation.op !== 'undo'
    ) {
      blocked('Entry is paused. Say “resume” when ready.')
      return
    }
    if (operation.op === 'stop') {
      control = 'stop'
      current = { ...current, pendingAdd: undefined }
      message = 'Stopped.'
      return
    }
    if (operation.op === 'pause') {
      current = { ...current, paused: true, pendingAdd: undefined }
      message = 'Entry is paused. Say “resume” when ready.'
      return
    }
    if (operation.op === 'resume') {
      current = { ...current, paused: false }
      message = `Listening on ${phaseLabel(currentPhases, current.phaseIndex)}.`
      return
    }
    if (operation.op === 'undo') {
      if (current.pendingAdd) {
        current = { ...current, pendingAdd: undefined, paused: false }
        message = 'Cleared the pending procedure.'
        return
      }
      const previous = current.history[current.history.length - 1]
      if (!previous) {
        blocked('Nothing to undo.')
        return
      }
      currentPhases = clonePhases(previous.phases)
      keys = [...previous.activeAccordionKeys]
      current = {
        ...current,
        phaseIndex: previous.phaseIndex,
        history: current.history.slice(0, -1),
        paused: false,
        pendingAdd: undefined,
      }
      phasesChanged = true
      message = 'Restored the previous treatment plan change.'
      return
    }
    if (operation.op === 'unsupported') {
      blocked(operation.reason)
      return
    }
    if (operation.op === 'search') {
      const phaseIndex = resolvePhaseIndex(
        currentPhases,
        undefined,
        current.phaseIndex,
      )
      if (typeof phaseIndex === 'string') {
        blocked(phaseIndex)
        return
      }
      keys = expandPhase(currentPhases, keys, phaseIndex)
      current = { ...current, phaseIndex, pendingAdd: undefined }
      pendingSearch = { phaseIndex, query: operation.query }
      message = `Searching for ${operation.query}.`
      return
    }
    if (operation.op === 'add_phase') {
      const history = pushHistory(current, currentPhases, keys)
      const phaseKey = createNewPhaseKey()
      const visibleCount = currentPhases.filter(
        (phase) => !phase.is_hidden,
      ).length
      const nextPhases = [
        ...currentPhases,
        createEmptyPhase(visibleCount, phaseKey),
      ]
      const phaseIndex = nextPhases.length - 1
      currentPhases = nextPhases
      keys = expandPhase(nextPhases, keys, phaseIndex)
      current = { ...current, phaseIndex, history, pendingAdd: undefined }
      phasesChanged = true
      message = `Added ${phaseLabel(nextPhases, phaseIndex)}.`
      return
    }
    if (operation.op === 'next_phase' || operation.op === 'previous_phase') {
      const visible = visibleIndexes(currentPhases)
      if (visible.length === 0) {
        blocked('Add a phase first.')
        return
      }
      const position = Math.max(0, visible.indexOf(current.phaseIndex))
      const nextPosition =
        operation.op === 'next_phase'
          ? Math.min(visible.length - 1, position + 1)
          : Math.max(0, position - 1)
      const phaseIndex = visible[nextPosition]
      keys = expandPhase(currentPhases, keys, phaseIndex)
      current = { ...current, phaseIndex, pendingAdd: undefined }
      message = `On ${phaseLabel(currentPhases, phaseIndex)}.`
      return
    }
    if (operation.op === 'jump_phase') {
      const phaseIndex = resolvePhaseIndex(
        currentPhases,
        operation.phase,
        current.phaseIndex,
      )
      if (typeof phaseIndex === 'string') {
        blocked(phaseIndex)
        return
      }
      keys = expandPhase(currentPhases, keys, phaseIndex)
      current = { ...current, phaseIndex, pendingAdd: undefined }
      message = `On ${phaseLabel(currentPhases, phaseIndex)}.`
      return
    }
    const phaseIndex = resolvePhaseIndex(
      currentPhases,
      'phase' in operation ? operation.phase : undefined,
      current.phaseIndex,
    )
    if (typeof phaseIndex === 'string') {
      blocked(phaseIndex)
      return
    }
    if (operation.op === 'set_status') {
      if (!context.isEditing) {
        pendingStatus = { phaseIndex, status: operation.status }
        current = { ...current, phaseIndex, pendingAdd: undefined }
        message = `Confirm ${operation.status.toLowerCase()} for ${phaseLabel(currentPhases, phaseIndex)}.`
        return
      }
      const history = pushHistory(current, currentPhases, keys)
      currentPhases = currentPhases.map((phase, index) =>
        index === phaseIndex ? { ...phase, status: operation.status } : phase,
      )
      keys = expandPhase(currentPhases, keys, phaseIndex)
      current = { ...current, phaseIndex, history, pendingAdd: undefined }
      phasesChanged = true
      message = `${phaseLabel(currentPhases, phaseIndex)} is ${operation.status.toLowerCase()}.`
      return
    }
    if (operation.op === 'remove_last') {
      const items = currentPhases[phaseIndex]?.items ?? []
      const lastVisible = [...items]
        .reverse()
        .findIndex((item) => !item.is_hidden)
      if (lastVisible === -1) {
        blocked(
          `No procedure to remove on ${phaseLabel(currentPhases, phaseIndex)}.`,
        )
        return
      }
      const removeAt = items.length - 1 - lastVisible
      const history = pushHistory(current, currentPhases, keys)
      currentPhases = currentPhases.map((phase, index) =>
        index === phaseIndex
          ? {
              ...phase,
              items: items.filter((_, itemIndex) => itemIndex !== removeAt),
            }
          : phase,
      )
      current = { ...current, phaseIndex, history, pendingAdd: undefined }
      phasesChanged = true
      message = `Removed the last procedure on ${phaseLabel(currentPhases, phaseIndex)}.`
      return
    }
    if (operation.op === 'add') {
      const teeth = operation.teeth ?? []
      const pending = current.pendingAdd
      const followUpSlots = peelSlots(
        normalizePhrase(operation.procedureQuery).split(' '),
      )
      const surfaceOnlyFollowUp =
        Boolean(pending) &&
        (operation.teeth?.length ?? 0) === 0 &&
        followUpSlots.unknown.length === 0 &&
        !followUpSlots.material &&
        followUpSlots.surfaces.length > 0
      const matched =
        operation.procedureQuery.trim() !== '' && !surfaceOnlyFollowUp
          ? matchProcedure(operation.procedureQuery, context.procedures, teeth)
          : { error: 'Say a procedure name or code.' }
      if (pending && ('error' in matched || surfaceOnlyFollowUp)) {
        const completed = completePendingAdd(pending, operation)
        const materialResult = requiredMaterial(
          completed.procedure,
          completed.matchedText,
          completed.material ?? undefined,
        )
        if (materialResult && typeof materialResult === 'object') {
          current = { ...current, pendingAdd: completed }
          message = materialResult.error
          return
        }
        const material =
          typeof materialResult === 'string'
            ? materialResult
            : completed.material
        const nextPending = {
          ...completed,
          material,
          missingFields: requiredMissingFields(
            completed.procedure,
            completed.teeth,
            completed.surfaces,
            material,
          ),
        }
        const surfaceError = surfacesAreValid(
          nextPending.procedure,
          nextPending.surfaces,
          nextPending.teeth.length,
        )
        if (nextPending.missingFields.length > 0) {
          current = {
            ...current,
            phaseIndex: nextPending.phaseIndex,
            pendingAdd: nextPending,
          }
          message = promptForPendingAdd(nextPending)
          return
        }
        if (surfaceError) {
          const stalled: TxPlanVoicePendingAdd = {
            ...nextPending,
            missingFields: nextPending.missingFields.includes('surface')
              ? nextPending.missingFields
              : [...nextPending.missingFields, 'surface'],
          }
          current = {
            ...current,
            phaseIndex: stalled.phaseIndex,
            pendingAdd: stalled,
          }
          message = `${pendingSelectionSummary(stalled)}. ${surfaceError}`
          return
        }
        const history = pushHistory(current, currentPhases, keys)
        const items = buildItems(
          nextPending.procedure,
          currentPhases[nextPending.phaseIndex]?.phase_id ?? null,
          nextPending.teeth,
          nextPending.surfaces,
          nextPending.material,
          context,
        )
        currentPhases = currentPhases.map((phase, index) =>
          index === nextPending.phaseIndex
            ? { ...phase, items: [...phase.items, ...items] }
            : phase,
        )
        keys = expandPhase(currentPhases, keys, nextPending.phaseIndex)
        current = {
          ...current,
          phaseIndex: nextPending.phaseIndex,
          history,
          pendingAdd: undefined,
        }
        phasesChanged = true
        const toothText =
          nextPending.teeth.length > 0
            ? `, tooth ${nextPending.teeth.join(', ')}`
            : ''
        const name =
          nextPending.procedure.procedure?.description ||
          nextPending.procedure.description
        message = `Added ${name} on ${phaseLabel(currentPhases, nextPending.phaseIndex)}${toothText}.`
        return
      }
      if ('error' in matched) {
        blocked(matched.error)
        return
      }
      const materialResult = requiredMaterial(
        matched.procedure,
        matched.matchedText,
        matched.material,
      )
      if (materialResult && typeof materialResult === 'object') {
        blocked(materialResult.error)
        return
      }
      const material =
        typeof materialResult === 'string' ? materialResult : null
      const missingFields = requiredMissingFields(
        matched.procedure,
        teeth,
        matched.surfaces,
        material,
      )
      const surfaceError = surfacesAreValid(
        matched.procedure,
        matched.surfaces,
        teeth.length,
      )
      if (missingFields.length > 0) {
        const pendingAdd: TxPlanVoicePendingAdd = {
          procedure: matched.procedure,
          phaseIndex,
          originalQuery: operation.procedureQuery,
          matchedText: matched.matchedText,
          teeth,
          surfaces: matched.surfaces,
          material,
          missingFields,
        }
        keys = expandPhase(currentPhases, keys, phaseIndex)
        current = { ...current, phaseIndex, pendingAdd }
        message = promptForPendingAdd(pendingAdd)
        return
      }
      if (surfaceError) {
        const pendingAdd: TxPlanVoicePendingAdd = {
          procedure: matched.procedure,
          phaseIndex,
          originalQuery: operation.procedureQuery,
          matchedText: matched.matchedText,
          teeth,
          surfaces: matched.surfaces,
          material,
          missingFields: ['surface'],
        }
        keys = expandPhase(currentPhases, keys, phaseIndex)
        current = { ...current, phaseIndex, pendingAdd }
        message = `${pendingSelectionSummary(pendingAdd)}. ${surfaceError}`
        return
      }
      const history = pushHistory(current, currentPhases, keys)
      const items = buildItems(
        matched.procedure,
        currentPhases[phaseIndex]?.phase_id ?? null,
        teeth,
        matched.surfaces,
        material,
        context,
      )
      currentPhases = currentPhases.map((phase, index) =>
        index === phaseIndex
          ? { ...phase, items: [...phase.items, ...items] }
          : phase,
      )
      keys = expandPhase(currentPhases, keys, phaseIndex)
      current = { ...current, phaseIndex, history, pendingAdd: undefined }
      phasesChanged = true
      const toothText = teeth.length > 0 ? `, tooth ${teeth.join(', ')}` : ''
      const name =
        matched.procedure.procedure?.description ||
        matched.procedure.description
      message = `Added ${name} on ${phaseLabel(currentPhases, phaseIndex)}${toothText}.`
    }
  })
  return {
    phases: currentPhases,
    activeAccordionKeys: keys,
    state: current,
    message,
    ...(control ? { control } : {}),
    phasesChanged,
    rejected,
    ...(pendingStatus ? { pendingStatus } : {}),
    ...(pendingSearch ? { pendingSearch } : {}),
  }
}
